// ─────────────────────────────────────────────────────────────────────────────
// Theme registry
//
// The app reads colors from the `Colors` object exported by `config/default.jsx`.
// Each theme below provides a full set of those color keys, so switching a theme
// is just a matter of swapping which color set `Colors` points at (see
// `config/default.jsx` → `applyThemeColors`).
//
// • The dark variants keep the original dark surfaces and only re-tint the brand
//   / accent family — they look complete everywhere out of the box.
// • The light theme flips surfaces + text. It covers all `Colors`-driven UI;
//   screens that hardcode dark hex values will need a follow-up pass.
// ─────────────────────────────────────────────────────────────────────────────

export const THEME_STORAGE_KEY = 'bh-app-theme';
export const DEFAULT_THEME_KEY = 'dark';

// Marks (in sessionStorage) that this browsing session has already resolved the
// theme once — set at login (signIn) and by the themeContext reconcile. Survives
// an in-session reload but is wiped on logout (sessionStorage.clear()), which is
// what separates a "fresh login" from an "in-session reload after a switch".
export const THEME_RECONCILE_FLAG = 'bh-theme-reconciled';

// ── Base dark palette (the app's original colors) ───────────────────────────
const darkColors = {
  BG_BLACK: '#141414',
  DARKBLUE: '#1AACAC',
  MUSTARD: '#EEBA6D',
  WHITE: '#fafafa',
  LIGHTGREY: '#BABABA',
  DEEPBLUE: '#24658D',
  TOURQUISE: '#2E97A7',
  NAV_BLACK: '#1E1E1E',
  TEXTGREY: '#fafafa',
  RED: '#E95050',
  DEEPGREY: '#585858',
  Green: '#17B169',
  Canvas_BG: '#282828',
  Canvas_Left_BG: '#161618',
  // Draggable seams between panes. DEEPGREY is only 2.07:1 on Canvas_BG, under
  // the 3:1 WCAG non-text floor; this clears it on both canvas grounds.
  Canvas_Seam: '#727272',
  LIGHTBLUE: '#429EB0',
  YELLOW: '#F1A230',
  GREY: '#262626',
  DISABLEDGRAY: '#A7A7A7',
  DARKGRAY: '#303030',
  QB_GREEN: '#2CA01C',
  LIGHT_TOURQUISE: '#5AB8C5',
  TEEL_BLUE: '#4682B4',
  BLAKISH_BLUE: '#1A2427',
  POLICY_BG: '#1C1C1C',
  OCR_BG: '#D1F9FF',
  OCR_BORDER: '#5CBBCA',
  SEA_GREEN: '#2CC6CB',
  DARK_SEA_GREEN: '#176C6F',
  SLATE_GREY: '#27282E',
  GRAPHITE_GREY: '#303138',
  DEEP_TEAL: '#263939',
  COCOA_BROWN: '#473004',
  ROSEWOOD: '#382525'
};

// ── Dark · Ocean Blue (re-tint brand family to blue/indigo) ─────────────────
const oceanColors = {
  ...darkColors,
  DARKBLUE: '#3B82F6',
  TOURQUISE: '#3B82F6',
  SEA_GREEN: '#38BDF8',
  LIGHT_TOURQUISE: '#60A5FA',
  LIGHTBLUE: '#5B9BD5',
  DEEPBLUE: '#1D4ED8',
  TEEL_BLUE: '#2563EB',
  DARK_SEA_GREEN: '#1E40AF',
  DEEP_TEAL: '#1E2A44',
  BLAKISH_BLUE: '#172033',
  OCR_BG: '#DCE9FF',
  OCR_BORDER: '#5B8DEF'
};

// ── Dark · Royal Violet ─────────────────────────────────────────────────────
const violetColors = {
  ...darkColors,
  DARKBLUE: '#8B5CF6',
  TOURQUISE: '#A78BFA',
  SEA_GREEN: '#C084FC',
  LIGHT_TOURQUISE: '#C4B5FD',
  LIGHTBLUE: '#A78BFA',
  DEEPBLUE: '#6D28D9',
  TEEL_BLUE: '#7C3AED',
  DARK_SEA_GREEN: '#5B21B6',
  DEEP_TEAL: '#2A2342',
  BLAKISH_BLUE: '#1E1A33',
  OCR_BG: '#ECE4FF',
  OCR_BORDER: '#A78BFA'
};

// ── Dark · Crimson Rose ─────────────────────────────────────────────────────
const roseColors = {
  ...darkColors,
  DARKBLUE: '#F43F5E',
  TOURQUISE: '#FB7185',
  SEA_GREEN: '#FB7185',
  LIGHT_TOURQUISE: '#FDA4AF',
  LIGHTBLUE: '#F87171',
  DEEPBLUE: '#BE123C',
  TEEL_BLUE: '#E11D48',
  DARK_SEA_GREEN: '#9F1239',
  DEEP_TEAL: '#3A2230',
  BLAKISH_BLUE: '#2E1A22',
  OCR_BG: '#FFE4E9',
  OCR_BORDER: '#FB7185'
};

// ── Light · Daylight (warm cream surfaces, teal accent) ─────────────────────
// Supporting palette: #FEFBF3 page, #F8F0DF cards, #9D9D9D muted grey.
const lightColors = {
  ...darkColors,
  // Surfaces — near-white page, slightly deeper cream cards (original)
  BG_BLACK: '#FEFBF3',
  NAV_BLACK: '#F8F0DF',
  GREY: '#F8F0DF',
  DARKGRAY: '#EFE6CF',
  Canvas_BG: '#F8F0DF',
  Canvas_Left_BG: '#FEFBF3',
  Canvas_Seam: '#7F7F7F',
  POLICY_BG: '#F8F0DF',
  SLATE_GREY: '#F8F0DF',
  GRAPHITE_GREY: '#EFE6CF',
  // Text (WHITE / TEXTGREY are used as foreground in dark → become dark ink here)
  WHITE: '#2B2A26',
  TEXTGREY: '#2B2A26',
  LIGHTGREY: '#6B6B6B',
  DEEPGREY: '#565656',
  DISABLEDGRAY: '#BDBDBD',
  // Brand / accent (teal, deeper for contrast on cream)
  DARKBLUE: '#0D9488',
  TOURQUISE: '#0F9AA3',
  SEA_GREEN: '#0FB5BB',
  LIGHT_TOURQUISE: '#14B8A6',
  LIGHTBLUE: '#2B8AA0',
  DEEPBLUE: '#155E63',
  TEEL_BLUE: '#2C7A7B',
  DARK_SEA_GREEN: '#0E6E71',
  DEEP_TEAL: '#DCEFEC',
  BLAKISH_BLUE: '#E8F3F1',
  OCR_BG: '#E6FBFF',
  OCR_BORDER: '#5CBBCA',
  // Functional
  RED: '#FF4E44',
  YELLOW: '#D97706',
  MUSTARD: '#B45309',
  Green: '#15803D',
  QB_GREEN: '#2CA01C',
  COCOA_BROWN: '#F3E7C8',
  ROSEWOOD: '#F6DFD3'
};

// ── Registry ────────────────────────────────────────────────────────────────
export const THEMES = {
  dark: {
    key: 'dark',
    label: 'Midnight Teal',
    description: 'The classic dark look',
    mode: 'dark',
    swatch: '#1AACAC',
    colors: darkColors
  },
  ocean: {
    key: 'ocean',
    label: 'Ocean Blue',
    description: 'Dark with a cool blue accent',
    mode: 'dark',
    swatch: '#3B82F6',
    colors: oceanColors
  },
  violet: {
    key: 'violet',
    label: 'Royal Violet',
    description: 'Dark with a vivid purple accent',
    mode: 'dark',
    swatch: '#8B5CF6',
    colors: violetColors
  },
  rose: {
    key: 'rose',
    label: 'Crimson Rose',
    description: 'Dark with a warm rose accent',
    mode: 'dark',
    swatch: '#F43F5E',
    colors: roseColors
  },
  light: {
    key: 'light',
    label: 'Daylight',
    description: 'Warm light theme with a teal accent',
    mode: 'light',
    swatch: '#0D9488',
    colors: lightColors
  }
};

export const THEME_LIST = Object.values(THEMES);
